import * as React from "react";
import * as ReactDOM from "react-dom";
import App from "../containers/App";
import StoreContainer from "./StoreContainer";
import { BootOptions } from "../types";
import ChatProvider from "../providers/ChatProvider";

const logStyle = "background: #ff00ef; color: #fff";

class BootStore {
  private _initialized = false;
  private _bootPromiseResolve: any;

  private _bootPromise = new Promise((resolve) => {
    this._bootPromiseResolve = resolve;
  });

  public async waitForBoot() {
    return this._bootPromise;
  }

  async boot(opts: BootOptions, callback?: Function) {
    console.log("%cvelaro:initializing", logStyle);

    if (this._initialized === true) {
      console.warn(
        "Boot was already called. This method should only be called once. If you're working with " +
        "a single page application and really want to call boot again, destroy the current " +
        "component first by calling Velaro('destroy')."
      );
      return;
    }

    this._initialized = true;

    StoreContainer.SettingStore.setSiteId(opts.siteId);
    StoreContainer.GroupStore.initializePageGroupId(opts.groupId || 0);

    this.initializeActiveGroupId();

    StoreContainer.SettingStore.setCustomVars(opts.customVars);
    StoreContainer.SettingStore.setClickToCall(opts.clickToCall || false);
    StoreContainer.SettingStore.setDisableInline(opts.disableInline || false);
    StoreContainer.SettingStore.setSkipPrechat(opts.skipPrechat || false);
    StoreContainer.SettingStore.setIsLGCanada(opts.isLGCanada || false);

    const hasPrivacyConsent = typeof opts.privacyConsent === "function";
    if (hasPrivacyConsent) {
      StoreContainer.SettingStore.setDisableCookies(true);

      opts.privacyConsent((consentGiven) => {
        console.log("Privacy consent given. Cookies enabled:", consentGiven);
        StoreContainer.SettingStore.setDisableCookies(!consentGiven);
      });
    }

    StoreContainer.SettingStore.setDisableLauncher(
      opts.disableLauncher || false
    );
    StoreContainer.SettingStore.setDisableVisitorMonitoring(
      opts.disableVisitorMonitoring || false
    );
    StoreContainer.SettingStore.setCookieDomain(opts.cookieDomain);

    if (opts.engagementId) {
      StoreContainer.EngagementStore.setEngagementId(opts.engagementId);
    }

    if (opts.visitorId) {
      StoreContainer.VisitorStore.setVisitorId(opts.visitorId);
    }

    if (opts.isPopout) {
      StoreContainer.FrameStore.setIsPopout(opts.isPopout);
      StoreContainer.FrameStore.setChatRequestedFromUrl(
        opts.chatRequestedFromUrl
      );
    }

    await StoreContainer.SettingStore.loadEndpoints();
    await StoreContainer.SettingStore.loadConfig();

    StoreContainer.PageHitStore.recordHit();
    StoreContainer.VisitorActionStore.addPageHit();

    await StoreContainer.VisitorStore.load();
    await StoreContainer.EngagementStore.loadSettings();
    await StoreContainer.AvailabilityStore.loadGroupAvailability();

    const container = this.getOrCreateContainer();

    const app = (
      <ChatProvider>
        <App container={container} siteId={opts.siteId} />
      </ChatProvider>
    );

    ReactDOM.render(app, container, () => {
      if (callback) {
        callback();
      }
    });

    this._bootPromiseResolve();

    console.log("%cvelaro:initialized", logStyle);
  }

  destroy() {
    console.log("%cvelaro:destroying", logStyle);

    const container = this.getContainer();

    if (container) {
      ReactDOM.unmountComponentAtNode(container);
    }

    this._initialized = false;

    StoreContainer.ParentWindowEventStore.dispose();
    StoreContainer.initialize();

    console.log("%cvelaro:destroyed", logStyle);
  }

  private initializeActiveGroupId() {
    const currentChatGroupId =
      StoreContainer.CookieStore.get("engagement_groupId");
    const engagementId = StoreContainer.CookieStore.get("engagementId");

    //A check to make sure we're not loading an old active group when the engagement is gone.
    const hasGroupCookieWithoutEngagement =
      engagementId === undefined && currentChatGroupId !== undefined;

    if (hasGroupCookieWithoutEngagement) {
      StoreContainer.CookieStore.set("engagement_groupId", undefined);
    } else {
      StoreContainer.GroupStore.initializeActiveGroupId(currentChatGroupId);
    }
  }

  private getContainer(): HTMLDivElement {
    return window.parent.document.getElementById(
      "velaro-container"
    ) as HTMLDivElement;
  }

  private getOrCreateContainer(): HTMLDivElement {
    const existingContainer = this.getContainer();

    if (existingContainer) {
      return existingContainer;
    }

    const container = document.createElement("div") as HTMLDivElement;
    container.id = "velaro-container";
    container.className = "velaro-cobrowse-block";
    container.setAttribute("role", "application");
    container.style.position = "fixed";
    container.style.width = "0";
    container.style.height = "0";
    container.style.bottom = "0";
    container.style.right = "0";
    container.style.zIndex = "2147483647";
    window.parent.document.body.appendChild(container);
    return container;
  }
}

export default BootStore;
